import { DeleteStages } from 'constants/deleteStages';
import { Resources } from 'constants/resources';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useGoBack } from 'hooks/useGoBack';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { useStagedDelete } from 'hooks/useStagedDelete';
import { MerchantsArchiveDialog } from 'pages/Merchants/components/MerchantsArchiveDialog/MerchantsArchiveDialog';
import { MerchantsUnarchiveDialog } from 'pages/Merchants/components/MerchantsUnarchiveDialog/MerchantsUnarchiveDialog';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { useNotify } from 'react-admin';
import { Button, CancelButton, DeleteButton, EditLinkButton } from 'shared/mui/Buttons';
import { Card, CardActionsButtons } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import { ContextMenu, ContextMenuEntityUserActionsButton } from 'shared/mui/ContextMenu';
import { DeleteStageDialog } from 'shared/mui/Dialogs/DeleteStageDialog/DeleteStageDialog';
import { DeleteStageDialogDenyReason } from 'shared/mui/Dialogs/DeleteStageDialog/DeleteStageDialogDenyReason';
import { IconButton } from 'shared/mui/IconButtons';
import { Typography, TypographyWithBold } from 'shared/mui/Typography';
import { TypographyBold } from 'shared/mui/Typography/TypographyBold';
import { CompanyShowDialog } from 'shared/widgets/CompanyShowDialog/CompanyShowDialog';
import { Company } from 'types/embedded/company';
import { Merchant } from 'types/embedded/merchant';
import { Permission } from 'types/embedded/permission';

import { MerchantsShowCommonInformationChipFields } from './MerchantsShowCommonInformationChipFields';
import { MerchantsShowCommonInformationTable } from './MerchantsShowCommonInformationTable';

type MerchantsShowCommonInformationProps = {
  record?: Merchant;
};

export const MerchantsShowCommonInformation: React.FC<MerchantsShowCommonInformationProps> = ({
  record,
}): JSX.Element => {
  const translate = useTypedTranslate();

  const { hasResourcePermissions, hasResourceAccess } = usePermissions();
  const { goBack } = useGoBack();
  const notify = useNotify();
  const { handleCloseRecordModal, openRecordModal, handleOpenRecordModal } =
    useRecordModal<Merchant>();
  const {
    record: company,
    handleOpenRecordModal: handleOpenCompanyModal,
    handleCloseRecordModal: handleCloseCompanyModal,
    openRecordModal: openCompanyModal,
  } = useRecordModal<Partial<Company>>();
  const {
    handleOpenRecordModal: handleOpenRecordModalUnarchive,
    openRecordModal: openRecordModalUnarchive,
    handleCloseRecordModal: handleCloseRecordModalUnarchive,
  } = useRecordModal<Merchant>();
  const {
    handleOpenRecordModal: handleOpenRecordModalArchive,
    openRecordModal: openRecordModalArchive,
    handleCloseRecordModal: handleCloseRecordModalArchive,
  } = useRecordModal<Merchant>();

  const { deleteOneRecord, denyDeleteReason, stage, setStage } = useStagedDelete({
    record,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['merchants'] });
      handleCloseRecordModal();
      setStage(DeleteStages.Initial);
      goBack();
      notify(
        <TypographyWithBold
          text={translate('app.merchants.merchantDeleted', { name: record?.name || '' })}
        />,
        { type: 'success' },
      );
    },
  });

  const handleCloseDeleteDialog = () => {
    setStage(DeleteStages.Initial);
    handleCloseRecordModal();
  };

  return (
    <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
      <CardHeader
        appendActions={
          hasResourceAccess(Resources.UserActions) && (
            <ContextMenu key="context-menu-button">
              <ContextMenuEntityUserActionsButton entityName="merchant" />
            </ContextMenu>
          )
        }
        prependActions={
          <IconButton onClick={goBack}>
            <ArrowBackIcon />
          </IconButton>
        }
        title={translate('app.common.generalInformation')}
        titleSize="smallBold"
      />
      <CardContent sx={{ paddingTop: '16px !important' }}>
        <MerchantsShowCommonInformationChipFields />
        <MerchantsShowCommonInformationTable
          handleOpenCompanyModal={handleOpenCompanyModal}
          record={record}
        />
      </CardContent>
      <CardActionsButtons
        rightActionsSlot={[
          hasResourcePermissions('merchants', [Permission.Full, Permission.Delete]) &&
            (record?.archive ? (
              <DeleteButton onClick={() => handleOpenRecordModal()} variant="outlined" />
            ) : (
              <DeleteButton
                label={translate('app.buttons.inArchive')}
                onClick={() => handleOpenRecordModalArchive()}
                variant="outlined"
              />
            )),
          hasResourcePermissions(Resources.Companies, [Permission.Full, Permission.Update]) &&
            (record?.archive ? (
              <Button
                key="archive-button"
                label={translate('app.buttons.restore')}
                onClick={() => handleOpenRecordModalUnarchive()}
                variant="contained"
              />
            ) : (
              <EditLinkButton variant="contained" />
            )),
        ]}
      />
      {openRecordModalUnarchive && record && (
        <MerchantsUnarchiveDialog
          onClose={handleCloseRecordModalUnarchive}
          open={openRecordModalUnarchive}
          record={record}
        />
      )}
      {openRecordModalArchive && record && (
        <MerchantsArchiveDialog
          onClose={handleCloseRecordModalArchive}
          open={openRecordModalArchive}
          record={record}
        >
          <CancelButton
            label={translate('app.merchants.backToMerchantsList')}
            linkType="back"
            sx={{ marginLeft: 'auto' }}
            variant="outlined"
          />
        </MerchantsArchiveDialog>
      )}
      <DeleteStageDialog
        deleteOneRecord={deleteOneRecord}
        details={
          <Typography>
            {translate('app.merchants.merchantWillBeDeletedPermanently1')}{' '}
            <TypographyBold fontSize={16} text={record?.name || ''} />{' '}
            {translate('app.merchants.merchantWillBeDeletedPermanently2')}
          </Typography>
        }
        onClose={handleCloseDeleteDialog}
        open={openRecordModal}
        stage={stage}
        title={translate('app.merchants.merchantDeletion')}
      >
        <DeleteStageDialogDenyReason reason={denyDeleteReason}>
          <CancelButton
            key="history-back-button"
            label={translate('app.merchants.backToMerchantsList')}
            onClick={handleCloseDeleteDialog}
            sx={{ marginLeft: 'auto' }}
            variant="outlined"
          />
        </DeleteStageDialogDenyReason>
      </DeleteStageDialog>
      <CompanyShowDialog
        company={company}
        onClose={handleCloseCompanyModal}
        open={openCompanyModal}
      />
    </Card>
  );
};
